




import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../services/axiosInstance";

// ================= GET ALL TASKS =================
export const fetchTasks = createAsyncThunk(
  "tasks/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get("/api/tasks");
      console.log("📋 Tasks API Response:", res.data);
      return res.data?.data || res.data || [];
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to fetch tasks"
      );
    }
  }
);

// ================= CREATE TASK =================
export const createTask = createAsyncThunk(
  "tasks/create",
  async (taskData, { rejectWithValue }) => {
    try {
      console.log("🔄 Creating task with data:", taskData);
      
      // Get first assigned user (backend supports single user)
      const assignedTo = taskData.assignedUsers && taskData.assignedUsers.length > 0 
        ? taskData.assignedUsers[0].userId 
        : null;

      const formattedData = {
        title: taskData.title,
        description: taskData.description || '',
        projectId: taskData.projectId,
        assignedTo: assignedTo,
        priority: taskData.priority || 'medium',
        status: taskData.status || 'pending',
        startDate: taskData.startDate || null,
        dueDate: taskData.dueDate || null
      };

      console.log("📤 Formatted data for API:", formattedData);

      const res = await axiosInstance.post("/api/tasks", formattedData);
      console.log("✅ Create task response:", res.data);
      
      return res.data?.data || res.data;
    } catch (err) {
      console.error("❌ Create task error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      return rejectWithValue(
        err.response?.data?.message || err.message || "Task creation failed"
      );
    }
  }
);

// ================= UPDATE TASK STATUS =================
export const updateTaskStatus = createAsyncThunk(
  "tasks/updateStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/api/tasks/${id}/status`, { status });
      return res.data?.data || res.data;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Status update failed"
      );
    }
  }
);

// ================= UPDATE TASK =================
export const updateTask = createAsyncThunk(
  "tasks/update",
  async ({ id, taskData }, { rejectWithValue }) => {
    try {
      console.log("🔄 Updating task:", id, taskData);
      
      // Get first assigned user
      const assignedTo = taskData.assignedUsers && taskData.assignedUsers.length > 0 
        ? taskData.assignedUsers[0].userId 
        : null;

      const formattedData = {
        title: taskData.title,
        description: taskData.description || '',
        projectId: taskData.projectId,
        assignedTo: assignedTo,
        priority: taskData.priority,
        status: taskData.status,
        startDate: taskData.startDate || null,
        dueDate: taskData.dueDate || null
      };

      console.log("📤 Formatted update data:", formattedData);
      
      const res = await axiosInstance.put(`/api/tasks/${id}`, formattedData);
      console.log("✅ Update task response:", res.data);
      return res.data?.data || res.data;
    } catch (err) {
      console.error("❌ Update task error:", err.response?.data || err.message);
      return rejectWithValue(
        err.response?.data?.message || err.message || "Task update failed"
      );
    }
  }
);

// ================= DELETE TASK =================
export const deleteTask = createAsyncThunk(
  "tasks/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`/api/tasks/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Delete failed"
      );
    }
  }
);

// ================= SLICE =================
const tasksSlice = createSlice({
  name: "tasks",
  initialState: {
    tasks: [],
    loading: false,
    error: null,
    lastAction: null
  },

  reducers: {
    clearTasks: (state) => {
      state.tasks = [];
      state.loading = false;
      state.error = null;
    },
    updateTaskLocal: (state, action) => {
      const index = state.tasks.findIndex(task => task._id === action.payload._id);
      if (index !== -1) {
        state.tasks[index] = action.payload;
      }
    },
    setLastAction: (state, action) => {
      state.lastAction = action.payload;
    }
  },

  extraReducers: (builder) => {
    builder
      // ================= FETCH ALL TASKS =================
      .addCase(fetchTasks.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastAction = 'fetchTasks.pending';
      })
      .addCase(fetchTasks.fulfilled, (state, action) => {
        state.loading = false;
        state.lastAction = 'fetchTasks.fulfilled';
        
        if (Array.isArray(action.payload)) {
          state.tasks = action.payload;
        } else if (action.payload && typeof action.payload === 'object') {
          state.tasks = [action.payload];
        } else {
          state.tasks = [];
        }
        
        console.log("📦 Tasks stored in redux:", state.tasks.length);
      })
      .addCase(fetchTasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.lastAction = 'fetchTasks.rejected';
      })

      // ================= CREATE TASK =================
      .addCase(createTask.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastAction = 'createTask.pending';
      })
      .addCase(createTask.fulfilled, (state, action) => {
        state.loading = false;
        state.lastAction = 'createTask.fulfilled';
        
        if (action.payload) {
          if (Array.isArray(action.payload)) {
            state.tasks = [...action.payload, ...state.tasks];
          } 
          else if (typeof action.payload === 'object') {
            state.tasks.unshift(action.payload);
          }
          
          console.log("✅ Task created, new count:", state.tasks.length);
        }
      })
      .addCase(createTask.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.lastAction = 'createTask.rejected';
        console.error("❌ Create task rejected:", action.payload);
      })

      // ================= UPDATE TASK STATUS =================
      .addCase(updateTaskStatus.fulfilled, (state, action) => {
        state.lastAction = 'updateTaskStatus.fulfilled';
        const updatedTask = action.payload;
        
        if (updatedTask && updatedTask._id) {
          const index = state.tasks.findIndex(task => task._id === updatedTask._id);
          if (index !== -1) {
            state.tasks[index] = updatedTask;
          }
        }
      })

      // ================= UPDATE TASK =================
      .addCase(updateTask.fulfilled, (state, action) => {
        state.loading = false;
        state.lastAction = 'updateTask.fulfilled';
        const updatedTask = action.payload;
        
        if (updatedTask && updatedTask._id) {
          const index = state.tasks.findIndex(task => task._id === updatedTask._id);
          if (index !== -1) {
            state.tasks[index] = updatedTask;
          }
        }
      })

      // ================= DELETE TASK =================
      .addCase(deleteTask.fulfilled, (state, action) => {
        state.lastAction = 'deleteTask.fulfilled';
        const deletedId = action.payload;
        state.tasks = state.tasks.filter(task => task._id !== deletedId);
      });
  }
});

export const { clearTasks, updateTaskLocal, setLastAction } = tasksSlice.actions;
export default tasksSlice.reducer;